import { message, Modal } from 'antd';
import { FlexCol } from '../../../components/common/common';
import { FC, useEffect, useState } from 'react';
import { SenseMode, SignatureWithSpotgrid } from '../../../types/types';
import MeasurementParameters from '../../../components/widgets/Parameters/MeasurementParameters';
import TimingParameters from '../../../components/widgets/Parameters/TimingParameters';
import { DEFAULT_ODOR_ZONES, OdorResult } from '../../ModelsManagementPage/Records/OdorResult';
import { getFullRecord, getFullRecords, RecordValueWithSensograms } from '../../../services/cache/idb';
import { loadHumidityCalibrant } from '../../../services/cache/localStorage';
import { useParams } from 'react-router-dom';
import { computeSignature, DEFAULT_SIGNATURE_PROCESSING_PARAMS, handleNormalisation } from '../../../components/analysis/signature';
import { euclideanDistance, mean } from '../../../components/analysis/utils';
import { PipelineType, ProcessingPipelineParams } from '../../../components/analysis/definitions';

type OdorResultsModal = {
  // modal parameters
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  recordKeyRef: React.MutableRefObject<string | null>;
  multiplicatorFactor: number;
};

// export const intensityScalingFactor = 2.5;

export const OdorResultsModal: FC<OdorResultsModal> = ({ isOpen, setIsOpen, recordKeyRef, multiplicatorFactor }) => {
  const onOk = async () => {
    setIsOpen(false);
    message.success('Odor recorded');
  };

  const onCancel = async () => {
    setIsOpen(false);
    message.success('Odor not recorded');
  };

  // tooling for building the intensity
  const [records, setRecords] = useState<RecordValueWithSensograms[]>([]);
  const [record, setRecord] = useState<RecordValueWithSensograms | null>(null);
  const [humidityCalibrant, setHumidityCalibrant] = useState<SignatureWithSpotgrid | undefined>(loadHumidityCalibrant());
  const [humidityDistance, setHumidityDistance] = useState<number | null>(null);
  const [recordIntensitySubstractedFromHumidityCalibrant, setRecordIntensitySubstractedFromHumidityCalibrant] = useState<number>(0);
  const { recordId } = useParams<{ recordId: string }>();

  const getRecordIntensity = (record: RecordValueWithSensograms, humidityCalibrant: SignatureWithSpotgrid): number => {
    const baselineBounds = [record.baselineStart, record.baselineEnd];
    const analyteBounds = [record.analyteStart, record.analyteEnd];
    let baselineHumidity = mean(record.humiditySeries.slice(...baselineBounds));
    let analyteHumidity = mean(record.humiditySeries.slice(...analyteBounds));
    let deltaH = analyteHumidity - baselineHumidity;
    // console.log('zones are', baselineBounds, analyteBounds);

    let { normalisation: normParam, ...processingParamNoNorm } = DEFAULT_SIGNATURE_PROCESSING_PARAMS;

    let finalProcessingParameters: ProcessingPipelineParams = {
      pipelineType: PipelineType.Classic,
      aggregation: processingParamNoNorm.aggregation,
    };

    if (humidityCalibrant) {
      // console.log('humidityCalibrant is', humidityCalibrant);
      // fetch delta H of the record
      finalProcessingParameters.humidityCorrectionParams = { deltaH: deltaH, calibrant: humidityCalibrant };
    }
    // console.log('finalProcessingparametrs', finalProcessingParameters);
    let [_correctedSignature] = computeSignature(record, finalProcessingParameters);
    // console.log('debug... zones are', )
    // console.log('finalProcessingparameters is', finalProcessingParameters);

    // console.log('corrected signature', _correctedSignature);

    return Math.max(..._correctedSignature);
  };

  const intensity = 12;
  const patientId = 'Smith';
  const date = Date.now();

  // Effect 1: fetch the main record
  useEffect(() => {
    if (recordKeyRef.current) {
      getFullRecord(recordKeyRef.current).then((_record) => {
        setRecord(_record);
      });
    }

    console.log('record key is', recordKeyRef.current, 'and humidity ref key is', humidityCalibrant?.recordKey);
  }, [recordKeyRef.current]);

  // Effect 2: once record + humidityCalibrant are both ready, fetch all records in one shot
  useEffect(() => {
    if (!humidityCalibrant?.recordKey || !record?.key) return;

    getFullRecords([humidityCalibrant.recordKey, record.key]).then((_records) => {
      _records.sort((a, b) => b.absoluteTimestamp - a.absoluteTimestamp);
      setRecords(_records);
    });

    console.log('test should not rerender');
  }, [record, humidityCalibrant]);

  // Effect 3: compute signatures + intensities once records are ready
  useEffect(() => {
    if (records.length === 0) return;

    try {
      let signatures: number[][] = [];
      let intensities: number[] = [];
      let spotsgrids1d: number[][] = [];

      let { normalisation: normParam, ...processingParamNoNorm } = DEFAULT_SIGNATURE_PROCESSING_PARAMS;

      for (let record_item of records) {
        let [_signature, _spotsgrid1d] = computeSignature(record_item, processingParamNoNorm);

        const normalizedSignature = normParam !== undefined ? handleNormalisation(_signature, _spotsgrid1d, normParam) : _signature;

        signatures.push(normalizedSignature);
        spotsgrids1d.push(_spotsgrid1d);
        intensities.push(mean(_signature));
      }

      // Validate all signatures have the same length
      const signatureLength = signatures[0].length;
      for (let sign of signatures) {
        if (sign.length !== signatureLength) throw new Error('signatures have different lengths');
      }

      // Validate all spotsgrids are identical
      const commonSpotsgrid = spotsgrids1d[0];
      for (let spotsgrid1d of spotsgrids1d) {
        if (spotsgrid1d.length !== commonSpotsgrid.length) throw new Error('spotsgrids have different lengths');
        for (let i = 0; i < spotsgrid1d.length; i++) {
          if (spotsgrid1d[i] !== commonSpotsgrid[i]) throw new Error('spotsgrids are not the same');
        }
      }

      // compute distance to humidity
      let distance = euclideanDistance(signatures[0], signatures[1]);
      let SCALING_FACTOR = 100;
      let DISPLAY_SCALE_FACTOR = 10 / 3;

      if (record !== null) {
        setHumidityDistance(DISPLAY_SCALE_FACTOR * (1 / 10) * parseInt((SCALING_FACTOR * distance).toFixed(2)));
      }
    } catch (e: any) {
      console.error(e);
    }

    // set odor intensity :
    if (record !== null && humidityCalibrant !== undefined) {
      let insensityCorrected = getRecordIntensity(record, humidityCalibrant);
      setRecordIntensitySubstractedFromHumidityCalibrant(multiplicatorFactor * insensityCorrected);
    }
  }, [records]);

  return (
    <Modal title="New Result" open={isOpen} onCancel={onCancel} onOk={onOk}>
      <FlexCol>{record?.absoluteTimestamp && record?.name && humidityDistance && <OdorResult intensity={recordIntensitySubstractedFromHumidityCalibrant} patientId={record.name} date={record?.absoluteTimestamp} zones={DEFAULT_ODOR_ZONES} />}</FlexCol>
    </Modal>
  );
};
